import { trpc } from "../lib/trpc";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Package, MapPin, Phone, Mail, Printer, ArrowLeft } from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import { useRoute, useLocation } from "wouter";
import { useTranslation } from "../i18n";
import { toast } from "sonner";

export default function ShippingOverview() {
  const { t } = useTranslation();
  const [, params] = useRoute("/orders/:id/shipping");
  const [, navigate] = useLocation();
  const orderId = params?.id ? parseInt(params.id) : undefined;

  const utils = trpc.useUtils();
  const { data: orderDetails, isLoading } = trpc.orders.getById.useQuery(
    { id: orderId || 0 },
    { enabled: !!orderId }
  );

  const markAsShippedMutation = trpc.orders.markAsShipped.useMutation({
    onSuccess: () => {
      toast.success(t.orders.markAsShipped);
      utils.orders.getById.invalidate({ id: orderId || 0 });
      utils.orders.list.invalidate();
    },
    onError: (error) => {
      toast.error(`Failed to mark as shipped: ${error.message}`);
    },
  });

  const handleMarkAsShipped = () => {
    if (!orderId) return;
    if (confirm("Mark this order as shipped?")) {
      markAsShippedMutation.mutate({ orderId });
    }
  };

  const handlePrintLabel = () => {
    if (!orderDetails) return;
    
    // Create a printable shipping label
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printItems = orderDetails.items.filter(item => item.productType === "print");
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t.orders.shippingLabel} - ${t.orders.orderNumber} ${orderDetails.orderNumber}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
          }
          .label-container {
            border: 2px solid #000;
            padding: 20px;
            margin-bottom: 20px;
          }
          .section {
            margin-bottom: 20px;
          }
          .section-title {
            font-weight: bold;
            font-size: 14px;
            margin-bottom: 10px;
            border-bottom: 1px solid #000;
            padding-bottom: 5px;
          }
          .address {
            font-size: 16px;
            line-height: 1.6;
          }
          .order-info {
            font-size: 12px;
          }
          .items-list {
            list-style: none;
            padding: 0;
          }
          .items-list li {
            padding: 5px 0;
          }
          @media print {
            body { padding: 0; }
            button { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="label-container">
          <div class="section">
            <div class="section-title">${t.orders.shippingLabel}</div>
            <div class="order-info">
              <strong>${t.orders.orderNumber}:</strong> ${orderDetails.orderNumber}<br>
              <strong>${t.common.date}:</strong> ${new Date().toLocaleDateString()}
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">${t.orders.shipTo}</div>
            <div class="address">
              <strong>${orderDetails.customerName || 'N/A'}</strong><br>
              ${orderDetails.customerAddressLine1 || ''}<br>
              ${orderDetails.customerAddressLine2 ? orderDetails.customerAddressLine2 + '<br>' : ''}
              ${orderDetails.customerPostalCode || ''} ${orderDetails.customerCity || ''}<br>
              ${orderDetails.customerCountry || ''}<br>
              ${orderDetails.customerPhone ? 'Tel: ' + orderDetails.customerPhone : ''}
            </div>
          </div>

          <div class="section">
            <div class="section-title">${t.orders.packageContents}</div>
            <ul class="items-list">
              ${printItems.map(item => `
                <li>${item.quantity}x ${item.printFormat || t.orders.standardPrint}</li>
              `).join('')}
            </ul>
          </div>
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Trigger print dialog immediately
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  if (!orderId) {
    return (
      <DashboardLayout>
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p>{t.orders.noOrderSelected}</p>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  // Filter only print items
  const printItems = orderDetails?.items.filter((item: any) => item.productType === "print") || [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header with Back Button */}
        <div>
          <Button
            variant="outline"
            onClick={() => navigate("/orders")}
            className="mb-4 gap-2 hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t.orders.backToOrders}
          </Button>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Package className="h-7 w-7 text-purple-600" />
            {t.orders.shippingDetails}
          </h1>
          <p className="text-gray-600">{t.orders.manageShipping}</p>
        </div>

        {/* Order Details */}
        {isLoading ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              {t.orders.loadingOrder}
            </CardContent>
          </Card>
        ) : !orderDetails ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>{t.orders.orderNotFound}</p>
            </CardContent>
          </Card>
        ) : printItems.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>{t.orders.noPrintItems}</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Order #{orderDetails.orderNumber}
                  </CardTitle>
                  <CardDescription>
                    {new Date(orderDetails.createdAt).toLocaleDateString()}
                  </CardDescription>
                </div>
                <Badge
                  variant={
                    orderDetails.status === "paid"
                      ? "default"
                      : orderDetails.status === "shipped"
                      ? "default"
                      : orderDetails.status === "pending"
                      ? "secondary"
                      : "outline"
                  }
                >
                  {t.orders[orderDetails.status as keyof typeof t.orders] || orderDetails.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Customer Info */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {t.orders.shippingAddress}
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-1">
                    <p className="font-medium">{orderDetails.customerName || "N/A"}</p>
                    {orderDetails.customerAddressLine1 && <p>{orderDetails.customerAddressLine1}</p>}
                    {orderDetails.customerAddressLine2 && <p>{orderDetails.customerAddressLine2}</p>}
                    {orderDetails.customerPostalCode && orderDetails.customerCity && (
                      <p>
                        {orderDetails.customerPostalCode} {orderDetails.customerCity}
                      </p>
                    )}
                    {orderDetails.customerCountry && <p>{orderDetails.customerCountry}</p>}
                  </div>

                  <div className="space-y-2">
                    {orderDetails.customerEmail && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="h-4 w-4" />
                        <a href={`mailto:${orderDetails.customerEmail}`} className="hover:underline">
                          {orderDetails.customerEmail}
                        </a>
                      </div>
                    )}
                    {orderDetails.customerPhone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="h-4 w-4" />
                        <a href={`tel:${orderDetails.customerPhone}`} className="hover:underline">
                          {orderDetails.customerPhone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Print Items */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <Printer className="h-4 w-4" />
                    {t.orders.printItems} ({printItems.length})
                  </h4>
                  <div className="space-y-2">
                    {printItems.map((item: any) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between bg-gray-50 p-3 rounded-lg text-sm"
                      >
                        <div>
                          <p className="font-medium">{item.printFormat || t.orders.standardPrint}</p>
                          <p className="text-xs text-gray-500">
                            Quantity: {item.quantity} × {parseFloat(item.price).toFixed(2)}{" "}
                            {orderDetails.currency}
                          </p>
                        </div>
                        <Badge variant="outline">{item.quantity}x</Badge>
                      </div>
                    ))}
                  </div>

                  <div className="pt-3 border-t">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">{t.orders.printItemsTotal}:</span>
                      <span className="text-lg font-bold">
                        {printItems.reduce((sum: number, item: any) => sum + (parseFloat(item.price) * item.quantity), 0).toFixed(2)} {orderDetails.currency}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 pt-6 border-t flex gap-3">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handlePrintLabel}
                >
                  <Printer className="h-4 w-4 mr-2" />
                  {t.orders.printShippingLabel}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleMarkAsShipped}
                  disabled={orderDetails.status === "shipped" || markAsShippedMutation.isPending}
                >
                  <Package className="h-4 w-4 mr-2" />
                  {orderDetails.status === "shipped" ? t.orders.shipped : t.orders.markAsShipped}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate(`/orders/${orderId}`)}>
                  {t.orders.viewFullOrder}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
